export type BrandKitSaveErrorCode =
  | "BRAND_ASSET_LIMIT"
  | "BRAND_KIT_NOT_FOUND"
  | "BRAND_KIT_FORBIDDEN"
  | "INVALID_BRAND_ASSET_CHANGES"
  | "BRAND_KIT_SAVE_FAILED"
  | "SESSION_EXPIRED"
  | "NETWORK_ERROR";

export class BrandKitSaveError extends Error {
  constructor(
    public readonly code: BrandKitSaveErrorCode,
    public readonly status: number | undefined,
    technicalMessage: string,
  ) {
    super(technicalMessage);
    this.name = "BrandKitSaveError";
  }
}

export type BrandKitAssetUploadErrorCode =
  | "INVALID_IMAGE_TYPE"
  | "IMAGE_TOO_LARGE"
  | "IMAGE_TOO_SMALL"
  | "INVALID_IMAGE"
  | "ASSET_UPLOAD_FAILED"
  | "SESSION_EXPIRED"
  | "BRAND_KIT_NOT_FOUND"
  | "BRAND_KIT_FORBIDDEN";

export class BrandKitAssetUploadError extends Error {
  constructor(
    public readonly code: BrandKitAssetUploadErrorCode,
    public readonly status: number | undefined,
    technicalMessage: string,
  ) {
    super(technicalMessage);
    this.name = "BrandKitAssetUploadError";
  }
}

export interface BrandKitSaveErrorCopy {
  title: string;
  description: string;
}

const supportedServerCodes = new Set<BrandKitSaveErrorCode>([
  "BRAND_ASSET_LIMIT",
  "BRAND_KIT_NOT_FOUND",
  "BRAND_KIT_FORBIDDEN",
  "INVALID_BRAND_ASSET_CHANGES",
  "BRAND_KIT_SAVE_FAILED",
]);

const supportedUploadCodes = new Set<BrandKitAssetUploadErrorCode>([
  "INVALID_IMAGE_TYPE",
  "IMAGE_TOO_LARGE",
  "IMAGE_TOO_SMALL",
  "INVALID_IMAGE",
  "ASSET_UPLOAD_FAILED",
  "BRAND_KIT_NOT_FOUND",
  "BRAND_KIT_FORBIDDEN",
]);

export function normalizeBrandKitSaveErrorCode(
  status: number,
  serverCode: unknown,
): BrandKitSaveErrorCode {
  if (
    typeof serverCode === "string" &&
    supportedServerCodes.has(serverCode as BrandKitSaveErrorCode)
  ) {
    return serverCode as BrandKitSaveErrorCode;
  }

  if (status === 401) return "SESSION_EXPIRED";
  if (status === 403) return "BRAND_KIT_FORBIDDEN";
  if (status === 404) return "BRAND_KIT_NOT_FOUND";
  if (status === 400) return "INVALID_BRAND_ASSET_CHANGES";
  return "BRAND_KIT_SAVE_FAILED";
}

export function normalizeBrandKitAssetUploadErrorCode(
  status: number,
  serverCode: unknown,
): BrandKitAssetUploadErrorCode {
  if (
    typeof serverCode === "string" &&
    supportedUploadCodes.has(serverCode as BrandKitAssetUploadErrorCode)
  ) {
    return serverCode as BrandKitAssetUploadErrorCode;
  }

  if (status === 401) return "SESSION_EXPIRED";
  if (status === 403) return "BRAND_KIT_FORBIDDEN";
  if (status === 404) return "BRAND_KIT_NOT_FOUND";
  return "ASSET_UPLOAD_FAILED";
}

function getErrorCode(error: unknown): string {
  if (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    typeof error.code === "string"
  ) {
    return error.code.toLowerCase();
  }

  return "";
}

export function getBrandKitSaveErrorCopy(
  error: unknown,
): BrandKitSaveErrorCopy {
  let code: BrandKitSaveErrorCode | undefined;

  if (error instanceof BrandKitSaveError) {
    code = error.code;
  } else {
    const errorCode = getErrorCode(error);
    const message = error instanceof Error ? error.message.toLowerCase() : "";

    if (
      errorCode.includes("unauthenticated") ||
      errorCode.startsWith("auth/") ||
      message.includes("session expired") ||
      message.includes("sign in again")
    ) {
      code = "SESSION_EXPIRED";
    } else if (errorCode.includes("permission-denied")) {
      code = "BRAND_KIT_FORBIDDEN";
    } else if (
      message.includes("failed to fetch") ||
      message.includes("networkerror") ||
      message.includes("network request failed") ||
      message.includes("load failed")
    ) {
      code = "NETWORK_ERROR";
    }
  }

  switch (code) {
    case "BRAND_ASSET_LIMIT":
      return {
        title: "Too many brand images",
        description:
          "A brand kit can contain up to 30 images. Remove an image and try again.",
      };
    case "SESSION_EXPIRED":
      return {
        title: "Sign in again to save",
        description:
          "Your session expired. Keep this editor open, sign in again, then try saving.",
      };
    case "BRAND_KIT_NOT_FOUND":
      return {
        title: "We couldn't find this brand kit",
        description:
          "It may have been deleted in another tab. Refresh the page and try again.",
      };
    case "BRAND_KIT_FORBIDDEN":
      return {
        title: "You can't edit this brand kit",
        description:
          "Sign in with the account that owns this brand kit, then try again.",
      };
    case "NETWORK_ERROR":
      return {
        title: "Check your connection",
        description:
          "We couldn't reach WaveGen. Your changes are still here, so you can try again.",
      };
    case "INVALID_BRAND_ASSET_CHANGES":
    case "BRAND_KIT_SAVE_FAILED":
    default:
      return {
        title: "We couldn't save your brand kit",
        description:
          "Your changes are still here. Please try again in a moment.",
      };
  }
}

export function getBrandKitAssetUploadErrorMessage(error: unknown): string {
  let code: BrandKitAssetUploadErrorCode | "NETWORK_ERROR" | undefined;

  if (error instanceof BrandKitAssetUploadError) {
    code = error.code;
  } else {
    const errorCode = getErrorCode(error);
    const message = error instanceof Error ? error.message.toLowerCase() : "";
    if (
      errorCode.includes("unauthenticated") ||
      errorCode.startsWith("auth/") ||
      message.includes("session expired") ||
      message.includes("sign in again")
    ) {
      code = "SESSION_EXPIRED";
    } else if (errorCode.includes("permission-denied")) {
      code = "BRAND_KIT_FORBIDDEN";
    } else if (
      message.includes("failed to fetch") ||
      message.includes("networkerror") ||
      message.includes("network request failed") ||
      message.includes("load failed")
    ) {
      code = "NETWORK_ERROR";
    }
  }

  switch (code) {
    case "INVALID_IMAGE_TYPE":
      return "Choose a PNG, JPG, or WebP image.";
    case "IMAGE_TOO_LARGE":
      return "This image is larger than 10 MB. Choose a smaller file.";
    case "IMAGE_TOO_SMALL":
      return "Choose an image that's at least 400 × 300 pixels.";
    case "INVALID_IMAGE":
      return "We couldn't read this image. Try another PNG, JPG, or WebP file.";
    case "SESSION_EXPIRED":
      return "Your session expired. Sign in again, then retry the upload.";
    case "BRAND_KIT_NOT_FOUND":
      return "We couldn't find this brand kit. Refresh the page and try again.";
    case "BRAND_KIT_FORBIDDEN":
      return "You can't add images to this brand kit. Sign in with the account that owns it.";
    case "NETWORK_ERROR":
      return "We couldn't upload this image. Check your connection and try again.";
    case "ASSET_UPLOAD_FAILED":
    default:
      return "We couldn't upload this image. Please try again.";
  }
}
